import type { ReactElement } from "react";
import { useId } from "react";

import { DropdownButton } from "@similarweb/ui-components";

import HooksModalV2Key from "app/constants/HooksModalV2Key";
import { translate } from "app/filters/i18nFilter";
import { CountrySelectCombobox } from "app/pages/sales-intelligence/common-components/dropdown/CountrySelectCombobox";
import { FormFieldLabel } from "app/pages/sales-intelligence/common-components/form-fields";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { StyledDropdownButtonWrapper } from "app/pages/sales-intelligence/modules/leads-search-filters/components/CountryDropdownButton/styles";
import {
    FilterContentContainer,
    StyledCountryDropdownContainer,
} from "app/pages/sales-intelligence/modules/leads-search-filters/components/filters/styles";
import type FindTopCountryFilter from "app/pages/sales-intelligence/modules/leads-search-filters/models/TopCountryFilter/TopCountryFilter";
import type { ICountryObject } from "app/services/CountryService";
import { openHooksModalV5 } from "app/services/ModalService";

import { COUNTRY_SELECT_WIDTH } from "../../../constants/index";

import { DROPDOWN_BUTTON, LABEL_COUNTRY, DROPDOWN_PLACEHOLDER } from "./const";

const TopCountryFilter = ({
    filter: { options },
    value,
    onFilterUpdate,
}: IFilterViewBaseProps<FindTopCountryFilter>): ReactElement | null => {
    const dropdownId = useId();

    // The first non-permitted country opens the divider that separates permitted from restricted rows.
    const firstRestrictedId = options.find((country) => !country.permitted)?.id;

    const handleCountrySelect = (country: ICountryObject) => {
        const currentValue = value ?? [];
        const updated = currentValue.includes(country.id)
            ? currentValue.filter((id) => id !== country.id)
            : [...currentValue, country.id];
        onFilterUpdate(updated);
    };

    const renderCountryDropdownButton = () => (
        // Inlined so Combobox.Trigger's `asChild` can merge its ref onto this styled.div.
        <StyledDropdownButtonWrapper>
            <DropdownButton>{translate(DROPDOWN_BUTTON)}</DropdownButton>
        </StyledDropdownButtonWrapper>
    );

    const restrictedCountryAction = (): void => {
        openHooksModalV5(HooksModalV2Key.WebCountry);
    };

    return (
        <FilterContentContainer>
            <FormFieldLabel text={translate(LABEL_COUNTRY)}>
                <StyledCountryDropdownContainer
                    id={dropdownId}
                    customWidth={COUNTRY_SELECT_WIDTH}
                >
                    <CountrySelectCombobox
                        multi
                        onSelect={handleCountrySelect}
                        onRestrictedClick={() => {
                            restrictedCountryAction();
                            return true;
                        }}
                        selectedIds={value ?? []}
                        items={options}
                        renderButton={renderCountryDropdownButton}
                        renderSeparatorBefore={(country) => country.id === firstRestrictedId}
                        searchPlaceHolder={translate(DROPDOWN_PLACEHOLDER)}
                    />
                </StyledCountryDropdownContainer>
            </FormFieldLabel>
        </FilterContentContainer>
    );
};

export default TopCountryFilter;
